import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { 
  Tag, AlertTriangle, CheckCircle2, XCircle, Search, Filter,
  DollarSign, Server, Database, HardDrive, Cloud, Download
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TaggingCompliance = () => {
  const { shouldShowData } = useDemoData();
  const [resources, setResources] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [complianceData, setComplianceData] = useState(null);

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tagging/compliance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setComplianceData(data);
        setResources(data.resources || []);
      }
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const requiredTags = complianceData?.requiredTags || ['Environment', 'Team', 'Project', 'Owner'];
  const compliantResources = resources.filter(r => r.compliant);
  const nonCompliantResources = resources.filter(r => !r.compliant);
  const complianceRate = complianceData?.complianceRate || ((compliantResources.length / resources.length) * 100).toFixed(1);
  const untaggedCost = complianceData?.untaggedCost || nonCompliantResources.reduce((sum, r) => sum + r.cost, 0);
  const totalCost = complianceData?.totalCost || resources.reduce((sum, r) => sum + r.cost, 0);

  const filteredResources = resources.filter(r => {
    const matchesSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         r.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'compliant' && r.compliant) ||
                         (filterStatus === 'non-compliant' && !r.compliant);
    return matchesSearch && matchesFilter;
  });

  const pieData = [
    { name: 'Compliant', value: compliantResources.length, color: '#10b981' },
    { name: 'Non-Compliant', value: nonCompliantResources.length, color: '#ef4444' }
  ];

  const tagCoverageData = requiredTags.map(tag => ({
    tag,
    covered: resources.filter(r => r.tags && r.tags[tag]).length,
    missing: resources.filter(r => !r.tags || !r.tags[tag]).length
  }));

  const getTypeIcon = (type) => {
    switch(type) {
      case 'EC2': case 'VM': case 'Compute': return <Server className="w-4 h-4" />;
      case 'RDS': case 'Database': return <Database className="w-4 h-4" />;
      case 'S3': case 'Blob': return <HardDrive className="w-4 h-4" />;
      default: return <Cloud className="w-4 h-4" />;
    }
  };

  const handleAutoTag = async (resourceId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/tagging/fix/${resourceId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Update local state optimistically
      setResources(resources.map(r => 
        r.id === resourceId 
          ? { ...r, compliant: true, missingTags: [], tags: { ...r.tags, Team: 'Unassigned', Project: 'Unknown', Owner: 'unknown@company.com', Environment: r.tags?.Environment || 'Unknown' } }
          : r
      ));
      toast.success('Resource tagged with default values');
    } catch (error) {
      console.error('Error fixing tags:', error);
      toast.error('Failed to apply tags');
    }
  };

  if (!shouldShowData) return <EmptyDataState title="Tagging Compliance" description="Connect your cloud accounts to monitor and enforce resource tagging policies." />;
  return (
    <div className="space-y-6" data-testid="tagging-compliance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <Tag className="w-7 h-7 text-emerald-500" />
            Tagging Compliance
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">
            Track and enforce resource tagging policies
          </p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700">
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`border-slate-200 dark:border-slate-800 ${parseFloat(complianceRate) < 70 ? 'bg-red-50 dark:bg-red-900/10' : 'bg-emerald-50 dark:bg-emerald-900/10'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Compliance Rate</p>
                <p className={`text-3xl font-bold ${parseFloat(complianceRate) < 70 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {complianceRate}%
                </p>
              </div>
              {parseFloat(complianceRate) >= 70 
                ? <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                : <AlertTriangle className="w-10 h-10 text-red-500" />
              }
            </div>
            <Progress value={parseFloat(complianceRate)} className="mt-3 h-2" />
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Non-Compliant</p>
                <p className="text-2xl font-bold text-red-600">{nonCompliantResources.length}</p>
                <p className="text-xs text-slate-400 mt-1">resources need tags</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Unallocated Cost</p>
                <p className="text-2xl font-bold text-amber-600">${untaggedCost.toLocaleString()}</p>
                <p className="text-xs text-slate-400 mt-1">{((untaggedCost/totalCost)*100).toFixed(1)}% of total</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Required Tags</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{requiredTags.length}</p>
                <p className="text-xs text-slate-400 mt-1">policy enforced</p>
              </div>
              <Tag className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle>Compliance Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle>Tag Coverage by Required Tag</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={tagCoverageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tag" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="covered" fill="#10b981" name="Covered" stackId="a" />
                <Bar dataKey="missing" fill="#ef4444" name="Missing" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Required Tags Reference */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Required Tags Policy</CardTitle>
          <CardDescription>All resources must have these tags for proper cost allocation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {requiredTags.map(tag => (
              <Badge key={tag} variant="outline" className="px-3 py-1">
                <Tag className="w-3 h-3 mr-1" />
                {tag}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Resources Table */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>Resources</CardTitle>
              <CardDescription>Click on non-compliant resources to add missing tags</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-[200px]"
                />
              </div>
              <div className="flex gap-1">
                {['all', 'compliant', 'non-compliant'].map(status => (
                  <Button
                    key={status}
                    variant={filterStatus === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus(status)}
                    className={filterStatus === status ? 'bg-emerald-600' : ''}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filteredResources.map(resource => (
              <div 
                key={resource.id}
                className={`p-4 rounded-lg border ${
                  resource.compliant 
                    ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900' 
                    : 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/10'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      resource.compliant ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'
                    }`}>
                      {getTypeIcon(resource.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 dark:text-white">{resource.name}</span>
                        <Badge variant="outline" className="text-xs">{resource.type}</Badge>
                        <Badge variant="outline" className="text-xs">{resource.provider}</Badge>
                      </div>
                      {resource.compliant ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(resource.tags).map(([key, value]) => (
                            <span key={key} className="text-xs bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 rounded">
                              {key}: {value}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-red-600">Missing:</span>
                          {resource.missingTags?.map(tag => (
                            <span key={tag} className="text-xs bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-slate-400">Monthly Cost</p>
                      <p className="font-semibold text-slate-900 dark:text-white">${resource.cost.toFixed(2)}</p>
                    </div>
                    {resource.compliant ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleAutoTag(resource.id)} className="text-red-600 border-red-300">
                        Fix Tags
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaggingCompliance;
